// @mui
import { Theme, styled } from '@mui/material/styles';
import {
  Box,
  Checkbox,
  SxProps,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { useMediaQuery } from '@mui/material';
// ----------------------------------------------------------------------

const visuallyHidden = {
  border: 0,
  margin: -1,
  padding: 0,
  width: '1px',
  height: '1px',
  overflow: 'hidden',
  position: 'absolute',
  whiteSpace: 'nowrap',
  clip: 'rect(0 0 0 0)',
} as const;

const CustomTableHeader = styled(TableHead)(() => ({
  '& th': {
    paddingTop: '10px',
    paddingBottom: '10px',
    backgroundImage: 'none',
    backgroundColor: 'transparent',
    borderBottom: '1px solid #2d2d3d',
    zIndex: 11,
  },
  '& .MuiTableCell-root:last-of-type': {
    borderRadius: 0,
    boxShadow: 'none',
  },
  '& .MuiTableCell-root:first-of-type': {
    borderRadius: 0,
    boxShadow: 'none',
  },
}));

// ----------------------------------------------------------------------

interface ILabel {
  id: string;
  label: string;
  labelVariant?: any;
  secondLabel?: string;
  secondLabelVariant?: any;
  secondLabelBold?: boolean;
  tooltip?: string;
  align?: any;
  disableSort?: boolean;
  width?: number | string;
  minWidth?: number | string;
  sticky?: boolean;
  leftScroll?: number;
  paddingLeft?: number;
}

type Props = {
  order?: 'asc' | 'desc';
  orderBy?: string;
  headLabel: ILabel[];
  rowCount?: number;
  numSelected?: number;
  onSort?: (id: string) => void;
  onSelectAllRows?: (checked: boolean) => void;
  sx?: SxProps<Theme>;
};

export default function TableHeadCustom({
  order,
  orderBy,
  rowCount = 0,
  headLabel,
  numSelected = 0,
  onSort,
  onSelectAllRows,
  sx,
}: Props) {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  return (
    <CustomTableHeader sx={{ backgroundColor: 'background.default', ...sx }}>
      <TableRow>
        {onSelectAllRows && (
          <TableCell padding="checkbox">
            <Checkbox
              indeterminate={numSelected > 0 && numSelected < rowCount}
              checked={rowCount > 0 && numSelected === rowCount}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                onSelectAllRows(event.target.checked)
              }
            />
          </TableCell>
        )}

        {headLabel.map((headCell) => (
          <TableCell
            key={headCell.id}
            align={isSmallScreen ? 'center' : headCell?.align || 'left'}
            sortDirection={orderBy === headCell.id ? order : false}
            style={{
              width: headCell.width,
              minWidth: headCell.minWidth,
              backgroundColor: theme?.palette?.background?.default,
              ...(headCell?.sticky && {
                zIndex: 12,
                left: headCell?.leftScroll || 0,
                align: 'center',
              }),
              ...(headCell?.paddingLeft && {
                paddingLeft: headCell?.paddingLeft,
              }),
            }}
          >
            {onSort && !headCell?.disableSort ? (
              <TableSortLabel
                hideSortIcon
                active={orderBy === headCell.id}
                direction={orderBy === headCell.id ? order : 'asc'}
                onClick={() => onSort(headCell.id)}
              >
                <Tooltip title={headCell?.tooltip || ''}>
                  <Box>
                    <Typography variant={headCell?.labelVariant || 'body1'}>
                      {headCell.label}
                    </Typography>
                    {headCell?.secondLabel && (
                      <Typography
                        variant={headCell.secondLabelVariant ?? 'body1'}
                        fontWeight={headCell?.secondLabelBold ? 'bold' : ''}
                      >
                        {headCell.secondLabel}
                      </Typography>
                    )}
                  </Box>
                </Tooltip>

                {orderBy === headCell.id ? (
                  <Box sx={{ ...visuallyHidden }}>
                    {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                  </Box>
                ) : null}
              </TableSortLabel>
            ) : (
              <Tooltip title={headCell?.tooltip || ''}>
                <Box>
                  <Typography variant="body2">{headCell.label}</Typography>
                  {headCell?.secondLabel && (
                    <Typography
                      variant={headCell.secondLabelVariant ?? 'body1'}
                      fontWeight={headCell?.secondLabelBold ? 'bold' : ''}
                    >
                      {headCell.secondLabel}
                    </Typography>
                  )}
                </Box>
              </Tooltip>
            )}
          </TableCell>
        ))}
      </TableRow>
    </CustomTableHeader>
  );
}
